import React from 'react';
import { Link } from 'react-router-dom';
import { AspectRatio } from './ui/aspect-ratio';
import { Badge } from './ui/badge';
import { motion } from 'framer-motion';
import { Play, Clock, Eye } from 'lucide-react';

export const VideoCard = ({ video }) => {
  if (!video) return null;

  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.38, ease: 'easeOut' }}
    >
      <Link 
        to={`/videos/${video.slug}`}
        data-testid="video-card" 
        className="group block h-full"
      >
        <div className="relative h-full overflow-hidden rounded-lg border border-border bg-[hsl(var(--surface-2))] transition-all duration-300 hover:shadow-[0_20px_60px_rgba(0,0,0,0.45)] hover:-translate-y-1">
          {/* Thumbnail */}
          <AspectRatio ratio={16/9}>
            <div className="relative overflow-hidden h-full">
              <img
                src={video.thumbnailUrl}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              
              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors duration-300">
                <div 
                  data-testid="video-play-button"
                  className="flex items-center justify-center w-16 h-16 rounded-full bg-primary shadow-[0_0_30px_rgba(255,68,102,0.5)] transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_0_40px_rgba(255,68,102,0.7)]"
                >
                  <Play className="h-7 w-7 text-white fill-white ml-1" />
                </div>
              </div>

              {/* Duration chip */}
              {video.durationSec && (
                <div 
                  data-testid="video-duration-chip"
                  className="absolute bottom-2 right-2 bg-black/80 backdrop-blur-sm px-2 py-1 rounded text-xs text-white font-ui flex items-center gap-1"
                >
                  <Clock className="h-3 w-3" />
                  {formatDuration(video.durationSec)}
                </div>
              )}
            </div>
          </AspectRatio>

          {/* Content */}
          <div className="p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {video.series && (
                <Badge 
                  className="px-2.5 py-1 rounded-full text-xs uppercase tracking-wide bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))] border border-[hsl(var(--primary)/0.3)] hover:bg-[hsl(var(--primary)/0.18)]"
                >
                  {video.series.replace('-', ' ')}
                </Badge>
              )}
              {video.views !== undefined && video.views > 0 && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Eye className="h-3 w-3" />
                  {video.views.toLocaleString()}
                </span>
              )}
            </div>

            <h3 className="font-display text-xl md:text-2xl font-semibold leading-tight mb-2 group-hover:text-primary transition-colors duration-200">
              {video.title}
            </h3>

            {video.dek && (
              <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                {video.dek}
              </p>
            )}

            {video.publishedAt && (
              <p className="text-xs text-muted-foreground mt-3">
                {formatDate(video.publishedAt)}
              </p>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};